import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Clock, 
  Play, 
  Square, 
  Coffee, 
  MapPin, 
  Timer,
  CalendarDays,
  TrendingUp,
  FolderKanban,
  Pencil
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserDashboard() {
  const { token, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [locationError, setLocationError] = useState(null);
  
  // Project selection state
  const [projects, setProjects] = useState([]);
  const [showClockInDialog, setShowClockInDialog] = useState(false);
  const [showChangeProjectDialog, setShowChangeProjectDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [taskDescription, setTaskDescription] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/time/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
      toast.error('Failed to load status');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
    fetchProjects();
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchProjects]);

  // Get geolocation
  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const handleClockInClick = () => {
    if (projects.length > 0) {
      setShowClockInDialog(true);
    } else {
      handleClockIn();
    }
  };

  const handleClockIn = async (projectId = null, task = null) => {
    setActionLoading(true);
    setShowClockInDialog(false);
    
    try {
      let locationData = null;
      try {
        locationData = await getLocation();
        setLocationError(null);
      } catch (err) {
        setLocationError('Location unavailable');
        console.warn('Location error:', err);
      }

      // Handle "none" as no project
      const finalProjectId = (projectId || selectedProject);
      const cleanProjectId = finalProjectId === 'none' ? null : finalProjectId || null;

      await axios.post(`${API}/time/clock-in`, 
        { 
          location: locationData,
          project_id: cleanProjectId,
          task_description: task || taskDescription || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Clocked in successfully!');
      setSelectedProject('');
      setTaskDescription('');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      let locationData = null;
      try {
        locationData = await getLocation();
      } catch (err) {
        console.warn('Location error:', err);
      }

      await axios.post(`${API}/time/clock-out`,
        { location: locationData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Clocked out successfully!');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartBreak = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/time/break/start`,
        { break_type: 'general' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Break started');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start break');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndBreak = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/time/break/end`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Break ended');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end break');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenChangeProject = () => {
    // Pre-fill with current project/task if exists
    if (status?.active_entry) {
      setSelectedProject(status.active_entry.project_id || 'none');
      setTaskDescription(status.active_entry.task_description || '');
    }
    setShowChangeProjectDialog(true);
  };

  const handleChangeProject = async () => {
    if (!status?.active_entry?.id) return;
    
    setActionLoading(true);
    setShowChangeProjectDialog(false);

    try {
      const cleanProjectId = selectedProject === 'none' ? null : selectedProject || null;

      await axios.put(`${API}/time/entries/${status.active_entry.id}/project`,
        { 
          project_id: cleanProjectId,
          task_description: taskDescription || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Project updated successfully!');
      setSelectedProject('');
      setTaskDescription('');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    } finally {
      setActionLoading(false);
    }
  };

  const formatHours = (hours) => {
    if (!hours && hours !== 0) return '--:--';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getStatusBadge = () => {
    if (!status) return null;
    
    if (status.is_on_break) {
      return <Badge className="status-break">On Break</Badge>;
    }
    if (status.is_clocked_in) {
      return <Badge className="status-active">Clocked In</Badge>;
    }
    return <Badge className="status-inactive">Not Clocked In</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="h-10 w-10 spinner border-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">Good {currentTime.getHours() < 12 ? 'Morning' : currentTime.getHours() < 18 ? 'Afternoon' : 'Evening'}, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-muted-foreground">{format(currentTime, 'EEEE, MMMM d, yyyy')}</p>
        </div>

        {/* Main Clock Card */}
        <Card className="glass-card overflow-hidden">
          <CardContent className="p-8">
            <div className="flex flex-col items-center space-y-8">
              {/* Current Time */}
              <div className="text-center">
                <div className="text-6xl font-bold tracking-tight mb-2">
                  {format(currentTime, 'h:mm a')}
                  <span className="text-2xl text-muted-foreground">{format(currentTime, ':ss')}</span>
                </div>
                {getStatusBadge()}
              </div>

              {/* Clock Button */}
              <div className="relative">
                {status?.is_clocked_in ? (
                  status?.is_on_break ? (
                    <Button
                      onClick={handleEndBreak}
                      disabled={actionLoading}
                      className="h-32 w-32 rounded-full text-lg font-bold btn-break animate-pulse-glow"
                      data-testid="end-break-btn"
                    >
                      {actionLoading ? (
                        <div className="h-6 w-6 spinner border-white" />
                      ) : (
                        <>
                          <Play className="h-8 w-8 mb-1" />
                          <span>Resume</span>
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="flex gap-4">
                      <Button
                        onClick={handleOpenChangeProject}
                        disabled={actionLoading}
                        className="h-24 w-24 rounded-full text-sm font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/25"
                        data-testid="change-project-btn"
                      >
                        <div className="flex flex-col items-center">
                          <FolderKanban className="h-6 w-6 mb-1" />
                          <span>Project</span>
                        </div>
                      </Button>
                      <Button
                        onClick={handleStartBreak}
                        disabled={actionLoading}
                        className="h-24 w-24 rounded-full text-sm font-bold btn-break"
                        data-testid="start-break-btn"
                      >
                        <div className="flex flex-col items-center">
                          <Coffee className="h-6 w-6 mb-1" />
                          <span>Break</span>
                        </div>
                      </Button>
                      <Button
                        onClick={handleClockOut}
                        disabled={actionLoading}
                        className="h-32 w-32 rounded-full text-lg font-bold btn-clock-out"
                        data-testid="clock-out-btn"
                      >
                        {actionLoading ? (
                          <div className="h-6 w-6 spinner border-white" />
                        ) : (
                          <div className="flex flex-col items-center">
                            <Square className="h-8 w-8 mb-1" />
                            <span>Clock Out</span>
                          </div>
                        )}
                      </Button>
                    </div>
                  )
                ) : (
                  <Button
                    onClick={handleClockInClick}
                    disabled={actionLoading}
                    className="h-32 w-32 rounded-full text-lg font-bold btn-clock-in animate-pulse-glow"
                    data-testid="clock-in-btn"
                  >
                    {actionLoading ? (
                      <div className="h-6 w-6 spinner border-white" />
                    ) : (
                      <div className="flex flex-col items-center">
                        <Play className="h-8 w-8 mb-1" />
                        <span>Clock In</span>
                      </div>
                    )}
                  </Button>
                )}
              </div>

              {/* Current Project Info Display */}
              {status?.is_clocked_in && status?.active_entry?.project_name && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50">
                  <div 
                    className="h-3 w-3 rounded-full" 
                    style={{ backgroundColor: status.active_entry.project_color || '#6366f1' }}
                  />
                  <span className="font-medium">{status.active_entry.project_name}</span>
                  {status.active_entry.task_description && (
                    <span className="text-muted-foreground">• {status.active_entry.task_description}</span>
                  )}
                </div>
              )}

              {/* Location Status */}
              {status?.is_clocked_in && status?.active_entry?.clock_in_location && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {status.active_entry.clock_in_location.latitude.toFixed(4)}, 
                    {status.active_entry.clock_in_location.longitude.toFixed(4)}
                  </span>
                </div>
              )}
              {locationError && (
                <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <MapPin className="h-4 w-4" />
                  <span>{locationError}</span>
                </div>
              )}

              {/* Clock In Time */}
              {status?.active_entry && (
                <div className="text-center text-muted-foreground">
                  <p>Clocked in at {format(new Date(status.active_entry.clock_in), 'h:mm a')}</p>
                  {status.active_entry.breaks?.length > 0 && (
                    <p className="text-sm">
                      {status.active_entry.breaks.length} break(s) taken
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-primary" data-testid="today-hours-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Timer className="h-4 w-4" />
                Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatHours(status?.today_hours)}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500" data-testid="weekly-hours-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <CalendarDays className="h-4 w-4" />
                This Week
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatHours(status?.weekly_hours)}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500" data-testid="breaks-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Coffee className="h-4 w-4" />
                Breaks Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{status?.active_entry?.breaks?.length || 0}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500" data-testid="status-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold capitalize">
                {status?.is_on_break ? 'On Break' : status?.is_clocked_in ? 'Working' : 'Off'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Breaks */}
        {status?.active_entry?.breaks?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Today&apos;s Breaks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {status.active_entry.breaks.map((brk, index) => (
                  <div key={brk.id || index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Coffee className="h-4 w-4 text-amber-500" />
                      <span className="capitalize">{brk.type || 'Break'}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {format(new Date(brk.start), 'h:mm a')}
                      {brk.end && ` - ${format(new Date(brk.end), 'h:mm a')}`}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Clock In Dialog with Project Selection */}
      <Dialog open={showClockInDialog} onOpenChange={setShowClockInDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clock In</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Project (optional)</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger data-testid="project-select">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No project</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="h-3 w-3 rounded-full" 
                          style={{ backgroundColor: project.color }}
                        />
                        {project.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Task Description (optional)</Label>
              <Input
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                placeholder="What are you working on?"
                data-testid="task-description-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowClockInDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => handleClockIn(selectedProject, taskDescription)}
                disabled={actionLoading}
                className="btn-clock-in"
                data-testid="confirm-clock-in-btn"
              >
                {actionLoading ? (
                  <div className="h-4 w-4 spinner border-white mr-2" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Clock In
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Project Dialog */}
      <Dialog open={showChangeProjectDialog} onOpenChange={setShowChangeProjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Project/Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Project</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger data-testid="change-project-select">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No project</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="h-3 w-3 rounded-full" 
                          style={{ backgroundColor: project.color }}
                        />
                        {project.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Task Description</Label>
              <Input
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                placeholder="What are you working on?"
                data-testid="change-task-description-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowChangeProjectDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleChangeProject}
                disabled={actionLoading}
                data-testid="confirm-change-project-btn"
              >
                {actionLoading ? (
                  <div className="h-4 w-4 spinner border-primary-foreground mr-2" />
                ) : (
                  <Pencil className="h-4 w-4 mr-2" />
                )}
                Update Project
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
